import { z } from 'zod';
import {
  zBoxDefId,
  zExpansionId,
  zFixtureId,
  zI18nKey,
  zMaterial,
  zMiniGameId,
  zRoomId,
  zRotation,
  zSceneId,
  zUnlockId,
  zWallFloorId,
  zWallSide,
  zZoneId,
} from './ids';
import { zCondition, zRewards } from './progression';

/** Grid rectangle, half-open: cells [x, x+w) × [y, y+h). */
export const zGridRect = z.object({
  x: z.number().int(),
  y: z.number().int(),
  w: z.number().int().positive(),
  h: z.number().int().positive(),
});
export type GridRect = z.output<typeof zGridRect>;

export const zRoomOpening = z.object({
  kind: z.enum(['window', 'door']),
  wall: zWallSide,
  offset: z.number().int().min(0),
  span: z.number().int().positive(),
});

export const zZoneDef = z.object({
  id: zZoneId,
  rects: z.array(zGridRect).min(1),
  /** Forbids layer 'object' (aisle, door clearance); floor rugs are still fine. */
  blocksObjects: z.boolean(),
});
export type ZoneDef = z.output<typeof zZoneDef>;

export const zRoomLayout = z.object({
  id: zRoomId,
  sceneId: zSceneId,
  size: z.object({ w: z.number().int().positive(), h: z.number().int().positive() }),
  floor: z.array(zGridRect).min(1),
  walls: z.object({ left: z.boolean(), right: z.boolean() }),
  openings: z.array(zRoomOpening),
  zones: z.array(zZoneDef),
  fixtures: z.array(
    z.object({
      itemId: zFixtureId,
      x: z.number().int(),
      y: z.number().int(),
      rotation: zRotation,
      movable: z.boolean(),
    }),
  ),
  expansions: z.array(
    z.object({
      id: zExpansionId,
      unlockId: zUnlockId,
      addRects: z.array(zGridRect).min(1),
      zones: z.array(zZoneDef).optional(),
    }),
  ),
  boxSpawns: z.array(z.object({ boxDefId: zBoxDefId, x: z.number().int(), y: z.number().int() })).optional(),
  customerPath: z
    .object({
      entry: z.object({ x: z.number().int(), y: z.number().int() }),
      checkout: z.object({ x: z.number().int(), y: z.number().int() }),
    })
    .optional(),
  catSpots: z.array(z.object({ x: z.number().int(), y: z.number().int() })).optional(),
  interactiveObjects: z
    .array(
      z.object({
        id: z.enum(['dusty_chest', 'packing_crate', 'mailbox', 'order_board', 'booking_board']),
        sprite: z.string(),
        // Outside the floor: frontLeft = past the far end of column `offset` (y = h edge), frontRight = past
        // the far end of row `offset` (x = w edge). left/right = on that wall (not drawn yet).
        anchor: z.object({
          side: z.enum(['frontLeft', 'frontRight', 'left', 'right']),
          offset: z.number().int(),
        }),
        requires: z.array(zCondition).optional(),
      }),
    )
    .optional(),
  camera: z.object({ interest: zGridRect, minZoom: z.number().positive(), maxZoom: z.number().positive() }),
  defaultWall: zWallFloorId,
  defaultFloor: zWallFloorId,
});
export type RoomLayoutInput = z.input<typeof zRoomLayout>;
export type RoomLayout = z.output<typeof zRoomLayout>;

/** Starter boxes and mail mini-boxes (content-schemas §3.7). */
export const zBoxDef = z.object({
  id: zBoxDefId,
  nameKey: zI18nKey,
  noteKey: zI18nKey.optional(),
  order: z.number().int().min(1),
  contents: zRewards.min(1),
  requiresMiniGame: z.object({ miniGameId: zMiniGameId, level: z.number().int().min(1) }).optional(),
  material: zMaterial,
  sprite: z.string(),
});
export type BoxDefInput = z.input<typeof zBoxDef>;
export type BoxDef = z.output<typeof zBoxDef>;
